import { useCallback, useEffect, useRef } from 'react'
import { useContentInteraction } from './useContentInteraction'
import { HOVER_THRESHOLD_MS } from './useContentInteraction'
import type { ContentInteraction } from './useContentInteraction'

export interface UseCardInteractionsProps<TItem> {
  contentId: string
  item: TItem
  itemIndex: number
  hoverStartTimeRef: React.MutableRefObject<Map<string, number>>
  impressionTimeoutRef: React.MutableRefObject<Map<string, NodeJS.Timeout>>
  hasTrackedImpression: React.MutableRefObject<Set<string>>
  createInteraction: (item: TItem, itemIndex: number) => ContentInteraction | null
  onClickTrack?: () => void
}

export interface UseIsolatedCardInteractionsProps<TItem> {
  contentId: string
  item: TItem
  itemIndex: number
  createInteraction: (item: TItem, itemIndex: number) => ContentInteraction | null
  onClickTrack?: () => void
}

export interface UseCardInteractionsReturn {
  handleMouseEnter: () => void
  handleMouseLeave: () => void
  handleClick: () => void
  handleInViewChange: (inView: boolean) => void
}

export interface UseIsolatedCardInteractionsReturn {
  handleMouseEnter: () => void
  handleMouseLeave: () => void
  handleClick: () => void
}

export function useCardInteractions<TItem>({
  contentId,
  item,
  itemIndex,
  hoverStartTimeRef,
  impressionTimeoutRef,
  hasTrackedImpression,
  createInteraction,
  onClickTrack,
}: UseCardInteractionsProps<TItem>): UseCardInteractionsReturn {
  const { trackInteraction } = useContentInteraction()

  const handleMouseEnter = useCallback(() => {
    if (!contentId) return
    hoverStartTimeRef.current.set(contentId, Date.now())
  }, [contentId, hoverStartTimeRef])

  const handleMouseLeave = useCallback(() => {
    if (!contentId) return

    const startTime = hoverStartTimeRef.current.get(contentId)
    if (!startTime) return

    const dwellTime = Date.now() - startTime

    if (dwellTime >= HOVER_THRESHOLD_MS) {
      const interaction = createInteraction(item, itemIndex)
      if (interaction) {
        trackInteraction(interaction.withHover(dwellTime))
      }
    }

    hoverStartTimeRef.current.delete(contentId)
  }, [contentId, hoverStartTimeRef, createInteraction, item, itemIndex, trackInteraction])

  const handleClick = useCallback(() => {
    // Allow for optional additional tracking (legacy analytics events)
    onClickTrack?.()

    const interaction = createInteraction(item, itemIndex)
    if (interaction) {
      trackInteraction(interaction.withPress())
    }
  }, [createInteraction, item, itemIndex, trackInteraction, onClickTrack])

  const handleInViewChange = useCallback(
    (inView: boolean) => {
      if (!contentId) return

      if (inView) {
        // Prevent duplicate timers for the same content
        if (impressionTimeoutRef.current.has(contentId) || hasTrackedImpression.current.has(contentId)) {
          return
        }

        const timeout = setTimeout(() => {
          // Double-check still hasn't been tracked (in case of race conditions)
          if (hasTrackedImpression.current.has(contentId)) return
          hasTrackedImpression.current.add(contentId)

          const interaction = createInteraction(item, itemIndex)
          if (interaction) {
            trackInteraction(interaction.withImpression())
          }

          // Clean up the timeout reference after tracking
          impressionTimeoutRef.current.delete(contentId)
        }, 1000)

        impressionTimeoutRef.current.set(contentId, timeout)
      } else {
        const timeout = impressionTimeoutRef.current.get(contentId)
        if (timeout) {
          clearTimeout(timeout)
          impressionTimeoutRef.current.delete(contentId)
        }
      }
    },
    [contentId, hasTrackedImpression, createInteraction, item, itemIndex, trackInteraction, impressionTimeoutRef],
  )

  return {
    handleMouseEnter,
    handleMouseLeave,
    handleClick,
    handleInViewChange,
  }
}

export function useIsolatedCardInteractions<TItem>({
  contentId,
  item,
  itemIndex,
  createInteraction,
  onClickTrack,
}: UseIsolatedCardInteractionsProps<TItem>): UseIsolatedCardInteractionsReturn {
  const { trackInteraction } = useContentInteraction()

  // Internal state for hover tracking
  const hoverStartTimeRef = useRef<number | null>(null)

  const handleMouseEnter = useCallback(() => {
    if (!contentId) return
    hoverStartTimeRef.current = Date.now()
  }, [contentId])

  const handleMouseLeave = useCallback(() => {
    if (!contentId || !hoverStartTimeRef.current) return

    const dwellTime = Date.now() - hoverStartTimeRef.current

    if (dwellTime >= HOVER_THRESHOLD_MS) {
      const interaction = createInteraction(item, itemIndex)
      if (interaction) {
        trackInteraction(interaction.withHover(dwellTime))
      }
    }

    hoverStartTimeRef.current = null
  }, [contentId, createInteraction, item, itemIndex, trackInteraction])

  const handleClick = useCallback(() => {
    onClickTrack?.()

    const interaction = createInteraction(item, itemIndex)
    if (interaction) {
      trackInteraction(interaction.withPress())
    }
  }, [createInteraction, item, itemIndex, trackInteraction, onClickTrack])

  return {
    handleMouseEnter,
    handleMouseLeave,
    handleClick,
  }
}

/**
 * Hook specifically for impression tracking with intersection observer.
 * Separates impression logic for cards that handle it via useIntersectionObserver.
 *
 * @example
 * ```tsx
 * const [cardRef, isVisible] = useIntersectionObserver({ threshold: 0.9 })
 * useImpressionTracking({
 *   isVisible,
 *   contentId: item.id,
 *   item,
 *   itemIndex,
 *   createInteraction,
 * })
 * ```
 */
export function useImpressionTracking<TItem>({
  isVisible,
  contentId,
  item,
  itemIndex,
  createInteraction,
}: {
  isVisible: boolean
  contentId: string
  item: TItem
  itemIndex: number
  createInteraction: (item: TItem, itemIndex: number) => ContentInteraction | null
}) {
  const { trackInteraction } = useContentInteraction()
  const impressionTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const hasTrackedImpression = useRef(false)

  // Track impression when the card becomes visible for at least 1 second
  useEffect(() => {
    if (!contentId || hasTrackedImpression.current) return

    if (isVisible) {
      if (impressionTimeoutRef.current) return // Already has pending timer

      impressionTimeoutRef.current = setTimeout(() => {
        if (hasTrackedImpression.current) return
        hasTrackedImpression.current = true

        const interaction = createInteraction(item, itemIndex)
        if (interaction) {
          trackInteraction(interaction.withImpression())
        }

        impressionTimeoutRef.current = null
      }, 1000)
    } else {
      // Clear timeout if item becomes not visible before 1 second
      if (impressionTimeoutRef.current) {
        clearTimeout(impressionTimeoutRef.current)
        impressionTimeoutRef.current = null
      }
    }

    return () => {
      if (impressionTimeoutRef.current) {
        clearTimeout(impressionTimeoutRef.current)
        impressionTimeoutRef.current = null
      }
    }
  }, [isVisible, contentId, item, itemIndex, createInteraction, trackInteraction])
}
